import { useEffect, useRef, useState } from "react";
import type { Lang } from "../lib/i18n";

const TOTAL_STEPS = 7;

// Per-step short labels. The career-path pick step was removed (now
// auto-resolved from the employee's job title/department), so this is a
// 7-step journey, not the original 8.
const STEP_LABELS: Record<number, { ar: string; en: string }> = {
  1: { ar: "تسجيل الدخول", en: "Sign in" },
  2: { ar: "الملف الشخصي", en: "Your profile" },
  3: { ar: "سياسات وإجراءات كادن", en: "Kaden policies" },
  4: { ar: "أسئلة القسم", en: "Department FAQ" },
  5: { ar: "خريطة الأنظمة", en: "Systems map" },
  6: { ar: "قائمة المراجعة", en: "Review checklist" },
  7: { ar: "اكتملت الرحلة", en: "Journey completed" },
};

// Approximate minutes remaining after each step (index = step).
const MINUTES_PER_STEP = [0, 1, 2, 5, 4, 3, 2, 0];

/** Total estimated journey length — shared with WelcomeScreen so the "~X min"
 *  promise on the very first screen always matches the progress bar's math. */
export const TOTAL_JOURNEY_MINUTES = MINUTES_PER_STEP.reduce((a, b) => a + b, 0);

/** Compact step order for the "what's coming up" preview on the Welcome screen. */
export const JOURNEY_STEPS = STEP_LABELS;

interface ProgressBarProps {
  step: number;
  lang: Lang;
  onJumpToStep?: (step: number) => void;
}

/** Fixed progress bar with clickable completed-step dots and a "Saved ✓" flash. */
export function ProgressBar({ step, lang, onJumpToStep }: ProgressBarProps) {
  const [savedFlash, setSavedFlash] = useState(false);
  const prevStep = useRef(step);

  useEffect(() => {
    if (step > prevStep.current && step > 1) {
      setSavedFlash(true);
      const timer = setTimeout(() => setSavedFlash(false), 2500);
      prevStep.current = step;
      return () => clearTimeout(timer);
    }
    prevStep.current = step;
  }, [step]);

  if (step < 1) return null;

  const pct = Math.round((step / TOTAL_STEPS) * 100);
  const label = STEP_LABELS[step] || { ar: "", en: "" };
  const labelText = lang === "ar" ? `الخطوة ${step} من ${TOTAL_STEPS}, ${label.ar}` : `Step ${step} of ${TOTAL_STEPS}, ${label.en}`;
  const minsLeft = MINUTES_PER_STEP.slice(step + 1).reduce((a, b) => a + b, 0);
  const timeHint = minsLeft > 0 ? (lang === "ar" ? `~ ${minsLeft} دقائق متبقية` : `~ ${minsLeft} min remaining`) : "";

  return (
    <div className="kd-progress-bar" role="progressbar" aria-valuemin={0} aria-valuemax={TOTAL_STEPS} aria-valuenow={step}>
      <div className="kd-progress-steps">
        {Array.from({ length: TOTAL_STEPS }, (_, i) => {
          const s = i + 1;
          const done = s < step;
          const active = s === step;
          const clickable = done && !!onJumpToStep;
          const label = lang === "ar" ? STEP_LABELS[s]?.ar : STEP_LABELS[s]?.en;
          return (
            <button
              key={s}
              type="button"
              className={`kd-prog-dot${done ? " done" : ""}${active ? " active" : ""}${clickable ? " is-clickable" : ""}`}
              onClick={clickable ? () => onJumpToStep!(s) : undefined}
              aria-disabled={!clickable}
              title={label}
              aria-label={label}
              aria-current={active ? "step" : undefined}
            />
          );
        })}
      </div>
      <div className="kd-progress-track">
        <div className="kd-progress-fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="kd-progress-label-row">
        <span className="kd-progress-label" dir="auto">{labelText}</span>
        {timeHint && <span className="kd-progress-time-hint">{timeHint}</span>}
        {savedFlash && (
          <span className="kd-progress-saved" role="status" aria-live="polite">
            {lang === "ar" ? "محفوظ ✓" : "Saved ✓"}
          </span>
        )}
      </div>
    </div>
  );
}
